import axios from 'axios';
import { API_BASE_URL } from '@config';
import { showMessage } from 'app/store/actions/fuse';

// Action Types
export const SET_CLOUD_ANALYZER_LOADING = 'CLOUD_ANALYZER/SET_LOADING';
export const SET_CLOUD_ANALYZER_ERROR = 'CLOUD_ANALYZER/SET_ERROR';
export const CLEAR_CLOUD_ANALYZER_ERROR = 'CLOUD_ANALYZER/CLEAR_ERROR';

// Credentials
export const SET_CREDENTIALS = 'CLOUD_ANALYZER/SET_CREDENTIALS';
export const SET_SELECTED_CREDENTIAL = 'CLOUD_ANALYZER/SET_SELECTED_CREDENTIAL';

// Inventory (single inventory per credential)
export const SET_INVENTORY = 'CLOUD_ANALYZER/SET_INVENTORY';
export const SET_INVENTORY_STATUS = 'CLOUD_ANALYZER/SET_INVENTORY_STATUS';
export const CLEAR_INVENTORY = 'CLOUD_ANALYZER/CLEAR_INVENTORY';

// Scanning
export const SCAN_START = 'CLOUD_ANALYZER/SCAN_START';
export const SCAN_COMPLETE = 'CLOUD_ANALYZER/SCAN_COMPLETE';
export const SCAN_FAILED = 'CLOUD_ANALYZER/SCAN_FAILED';

// Cleanup
export const CLEANUP_START = 'CLOUD_ANALYZER/CLEANUP_START';
export const CLEANUP_PROGRESS = 'CLOUD_ANALYZER/CLEANUP_PROGRESS';
export const CLEANUP_RESOURCE_RESULT = 'CLOUD_ANALYZER/CLEANUP_RESOURCE_RESULT';
export const CLEANUP_COMPLETE = 'CLOUD_ANALYZER/CLEANUP_COMPLETE';
export const CLEANUP_RESET = 'CLOUD_ANALYZER/CLEANUP_RESET';
export const SET_DEPENDENCIES = 'CLOUD_ANALYZER/SET_DEPENDENCIES';
export const CLEAR_DEPENDENCIES = 'CLOUD_ANALYZER/CLEAR_DEPENDENCIES';

// Snapshots
export const SET_SNAPSHOTS = 'CLOUD_ANALYZER/SET_SNAPSHOTS';

// AI Analysis
export const AI_STATUS_SET = 'CLOUD_ANALYZER/AI_STATUS_SET';
export const AI_ANALYSIS_START = 'CLOUD_ANALYZER/AI_ANALYSIS_START';
export const AI_ANALYSIS_SUCCESS = 'CLOUD_ANALYZER/AI_ANALYSIS_SUCCESS';
export const AI_ANALYSIS_FAILED = 'CLOUD_ANALYZER/AI_ANALYSIS_FAILED';
export const AI_ANALYSIS_CLEAR = 'CLOUD_ANALYZER/AI_ANALYSIS_CLEAR';

// Headers for admin requests
var headers = {
	headers: {
		admin: true
	}
};

// ==================== Action Creators ====================

export function setLoading(loading) {
	return { type: SET_CLOUD_ANALYZER_LOADING, payload: loading };
}

export function setError(error) {
	return { type: SET_CLOUD_ANALYZER_ERROR, payload: error };
}

export function clearError() {
	return { type: CLEAR_CLOUD_ANALYZER_ERROR };
}

export function setCredentials(data) {
	return { type: SET_CREDENTIALS, payload: data };
}

export function setSelectedCredential(credential) {
	return { type: SET_SELECTED_CREDENTIAL, payload: credential };
}

export function setInventory(inventory) {
	return { type: SET_INVENTORY, payload: inventory };
}

export function setInventoryStatus(status) {
	return { type: SET_INVENTORY_STATUS, payload: status };
}

export function clearInventory() {
	return { type: CLEAR_INVENTORY };
}

export function setSnapshots(snapshots) {
	return { type: SET_SNAPSHOTS, payload: snapshots };
}

// ==================== Thunks ====================

/**
 * Get available credentials for cloud analysis
 */
export function getCredentials() {
	return function(dispatch) {
		dispatch(setLoading(true));
		
		var request = axios.get(API_BASE_URL + '/api/cloud-analyzer/credentials', headers);
		
		return request
			.then(function(response) {
				dispatch(setCredentials(response.data));
				dispatch(setLoading(false));
			})
			.catch(function(err) {
				var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to fetch credentials' };
				dispatch(setError(errData));
				dispatch(setLoading(false));
			});
	};
}

/**
 * Get inventory for a specific credential
 */
export function getInventory(credentialId) {
	return function(dispatch) {
		dispatch(setLoading(true));
		
		var request = axios.get(API_BASE_URL + '/api/cloud-analyzer/inventory/' + credentialId, headers);
		
		return request
			.then(function(response) {
				dispatch(setInventory(response.data));
				dispatch(setLoading(false));
			})
			.catch(function(err) {
				var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to fetch inventory' };
				dispatch(setError(errData));
				dispatch(setLoading(false));
			});
	};
}

/**
 * Start a scan for a credential (async - triggers background job)
 */
export function startScan(credentialId) {
	return function(dispatch) {
		dispatch({ type: SCAN_START });
		dispatch(setInventoryStatus('scanning'));
		
		var request = axios.post(API_BASE_URL + '/api/cloud-analyzer/scan/' + credentialId, {}, headers);
		
		return request
			.then(function(response) {
				dispatch(showMessage({
					message: 'Scan started. This may take a few minutes.',
					variant: 'info'
				}));
				return response.data;
			})
			.catch(function(err) {
				var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to start scan' };
				dispatch({ type: SCAN_FAILED, payload: errData.message });
				dispatch(setError(errData));
				dispatch(showMessage({
					message: errData.message || 'Failed to start scan',
					variant: 'error'
				}));
				throw err;
			});
	};
}

/**
 * Poll scan status until complete
 */
export function pollScanStatus(credentialId, pollIntervalMs) {
	if (typeof pollIntervalMs === 'undefined') pollIntervalMs = 3000;
	
	return function(dispatch) {
		var request = axios.get(API_BASE_URL + '/api/cloud-analyzer/scan/' + credentialId + '/status', headers);
		
		return request.then(function(response) {
			var status = response.data.status;
			
			if (status === 'idle' || status === 'completed') {
				dispatch({ type: SCAN_COMPLETE });
				// Fetch full inventory on completion
				dispatch(getInventory(credentialId));
				dispatch(showMessage({
					message: 'Scan complete!',
					variant: 'success'
				}));
				return { complete: true, status: status };
			} else if (status === 'failed') {
				dispatch({ type: SCAN_FAILED, payload: response.data.statusMessage });
				dispatch(setError({ message: response.data.statusMessage }));
				dispatch(showMessage({
					message: 'Scan failed: ' + response.data.statusMessage,
					variant: 'error'
				}));
				return { complete: true, status: status, error: response.data.statusMessage };
			} else {
				// Still scanning
				dispatch(setInventoryStatus(status));
				return { complete: false, status: status };
			}
		});
	};
}

/**
 * Cleanup a single resource
 */
export function cleanupResource(credentialId, resource) {
	return function(dispatch) {
		var payload = {
			provider: resource.provider || 'aws',
			resourceType: resource.resourceType || resource.type,
			resourceId: resource.resourceId,
			region: resource.region,
			credentialId: credentialId,
			details: resource.details || {}
		};
		
		dispatch({
			type: CLEANUP_PROGRESS,
			payload: { resourceId: resource.resourceId, status: 'in_progress' }
		});
		
		var request = axios.post(API_BASE_URL + '/api/cloud-analyzer/cleanup/resource', payload, headers);
		
		return request
			.then(function(response) {
				dispatch({
					type: CLEANUP_RESOURCE_RESULT,
					payload: {
						resourceId: resource.resourceId,
						success: true,
						message: response.data.message
					}
				});
				return response.data;
			})
			.catch(function(err) {
				var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to delete resource' };
				dispatch({
					type: CLEANUP_RESOURCE_RESULT,
					payload: {
						resourceId: resource.resourceId,
						success: false,
						message: errData.message
					}
				});
				throw err;
			});
	};
}

/**
 * Cleanup multiple resources
 */
export function cleanupResources(credentialId, resources) {
	return function(dispatch) {
		dispatch({
			type: CLEANUP_START,
			payload: { total: resources.length }
		});
		
		// Process resources sequentially
		var results = [];
		var index = 0;
		
		function processNext() {
			if (index >= resources.length) {
				var successCount = results.filter(function(r) { return r.success; }).length;
				dispatch({
					type: CLEANUP_COMPLETE,
					payload: { results: results }
				});
				dispatch(showMessage({
					message: 'Cleanup complete: ' + successCount + ' of ' + results.length + ' resources deleted',
					variant: successCount === results.length ? 'success' : 'warning'
				}));
				// Refresh inventory
				dispatch(getInventory(credentialId));
				return Promise.resolve(results);
			}
			
			var resource = resources[index];
			index++;
			
			return dispatch(cleanupResource(credentialId, resource))
				.then(function(result) {
					results.push({ resourceId: resource.resourceId, success: true, message: result.message });
					return processNext();
				})
				.catch(function(err) {
					var errMsg = err.response && err.response.data ? err.response.data.message : 'Failed';
					results.push({ resourceId: resource.resourceId, success: false, message: errMsg });
					return processNext();
				});
		}
		
		return processNext();
	};
}

/**
 * Reset cleanup state
 */
export function resetCleanup() {
	return { type: CLEANUP_RESET };
}

/**
 * Check dependencies for a resource before deletion
 */
export function checkDependencies(credentialId, resource) {
	return function(dispatch) {
		var payload = {
			provider: resource.provider || 'aws',
			resourceType: resource.resourceType || resource.type,
			resourceId: resource.resourceId,
			region: resource.region,
			credentialId: credentialId
		};
		
		var request = axios.post(API_BASE_URL + '/api/cloud-analyzer/cleanup/dependencies', payload, headers);
		
		return request
			.then(function(response) {
				dispatch({
					type: SET_DEPENDENCIES,
					payload: {
						resourceId: resource.resourceId,
						dependencies: response.data.dependencies || [],
						hasDependencies: response.data.hasDependencies || false,
						message: response.data.message
					}
				});
				return response.data;
			})
			.catch(function(err) {
				console.error('Failed to check dependencies:', err);
				dispatch({
					type: SET_DEPENDENCIES,
					payload: {
						resourceId: resource.resourceId,
						dependencies: [],
						hasDependencies: false,
						error: err.response && err.response.data ? err.response.data.message : 'Failed to check dependencies'
					}
				});
				throw err;
			});
	};
}

/**
 * Clear dependencies state
 */
export function clearDependencies() {
	return { type: CLEAR_DEPENDENCIES };
}

/**
 * Cascade delete a resource and all its dependencies
 */
export function cascadeDeleteResource(credentialId, resource) {
	return function(dispatch) {
		dispatch({
			type: CLEANUP_START,
			payload: { total: 1, isCascade: true }
		});
		
		var payload = {
			provider: resource.provider || 'aws',
			resourceType: resource.resourceType || resource.type,
			resourceId: resource.resourceId,
			region: resource.region,
			credentialId: credentialId,
			details: resource.details || {}
		};
		
		dispatch({
			type: CLEANUP_PROGRESS,
			payload: { resourceId: resource.resourceId, status: 'in_progress', message: 'Cascade delete in progress...' }
		});
		
		var request = axios.post(API_BASE_URL + '/api/cloud-analyzer/cleanup/cascade', payload, headers);
		
		return request
			.then(function(response) {
				var results = response.data.results || [];
				var successCount = results.filter(function(r) { return r.success; }).length;
				
				dispatch({
					type: CLEANUP_COMPLETE,
					payload: { results: results }
				});
				
				dispatch(showMessage({
					message: 'Cascade delete complete: ' + successCount + ' of ' + results.length + ' resources deleted',
					variant: successCount === results.length ? 'success' : 'warning'
				}));
				
				// Refresh inventory
				dispatch(getInventory(credentialId));
				
				return response.data;
			})
			.catch(function(err) {
				var errMsg = err.response && err.response.data ? err.response.data.message : 'Cascade delete failed';
				
				dispatch({
					type: CLEANUP_COMPLETE,
					payload: { 
						results: [{ 
							resourceId: resource.resourceId, 
							success: false, 
							message: errMsg 
						}] 
					}
				});
				
				dispatch(showMessage({
					message: errMsg,
					variant: 'error'
				}));
				
				throw err;
			});
	};
}

/**
 * Get historical snapshots for a credential
 */
export function getSnapshots(credentialId) {
	return function(dispatch) {
		var request = axios.get(API_BASE_URL + '/api/cloud-analyzer/snapshots/' + credentialId, headers);
		
		return request
			.then(function(response) {
				dispatch(setSnapshots(response.data.snapshots || []));
			})
			.catch(function(err) {
				console.error('Failed to fetch snapshots:', err);
			});
	};
}

/**
 * Create a snapshot of current inventory
 */
export function createSnapshot(credentialId, notes) {
	return function(dispatch) {
		var request = axios.post(
			API_BASE_URL + '/api/cloud-analyzer/snapshots/' + credentialId,
			{ notes: notes || '' },
			headers
		);
		
		return request
			.then(function(response) {
				dispatch(showMessage({
					message: 'Snapshot created successfully',
					variant: 'success'
				}));
				// Refresh snapshots
				dispatch(getSnapshots(credentialId));
				return response.data;
			})
			.catch(function(err) {
				var errData = err.response && err.response.data ? err.response.data : { message: 'Failed to create snapshot' };
				dispatch(showMessage({
					message: errData.message,
					variant: 'error'
				}));
				throw err;
			});
	};
}

// ==================== AI Analysis Actions ====================

export function setAIStatus(status) {
	return { type: AI_STATUS_SET, payload: status };
}

export function aiAnalysisStart() {
	return { type: AI_ANALYSIS_START };
}

export function aiAnalysisSuccess(data) {
	return { type: AI_ANALYSIS_SUCCESS, payload: data };
}

export function aiAnalysisFailed(error) {
	return { type: AI_ANALYSIS_FAILED, payload: error };
}

export function clearAIAnalysis() {
	return { type: AI_ANALYSIS_CLEAR };
}

/**
 * Check if AI analysis is available AND connected
 * Tests actual connectivity with quick timeout
 */
export function checkAIStatus() {
	return function(dispatch) {
		var request = axios.get(API_BASE_URL + '/api/cloud-analyzer/ai/status', headers);
		
		return request
			.then(function(response) {
				dispatch(setAIStatus({
					available: response.data.available,
					configured: response.data.configured,
					connected: response.data.available, // available means connected
					latency: response.data.latency,
					provider: response.data.provider,
					error: response.data.error,
					message: response.data.message
				}));
				return response.data;
			})
			.catch(function(err) {
				dispatch(setAIStatus({
					available: false,
					configured: false,
					connected: false,
					error: 'Failed to check AI status',
					message: 'Failed to check AI status'
				}));
				return { available: false };
			});
	};
}

/**
 * Get cached AI analysis if available
 */
export function getCachedAIAnalysis(credentialId) {
	return function(dispatch) {
		var request = axios.get(API_BASE_URL + '/api/cloud-analyzer/ai/cached/' + credentialId, headers);
		
		return request
			.then(function(response) {
				if (response.data.cached) {
					dispatch(aiAnalysisSuccess({
						analysis: response.data.analysis,
						timestamp: response.data.timestamp,
						cached: true,
						age: response.data.age
					}));
				}
				return response.data;
			})
			.catch(function(err) {
				console.error('Failed to fetch cached AI analysis:', err);
				return { cached: false };
			});
	};
}

/**
 * Generate AI analysis for current inventory
 */
export function generateAIAnalysis(credentialId) {
	return function(dispatch) {
		dispatch(aiAnalysisStart());
		
		var request = axios.post(
			API_BASE_URL + '/api/cloud-analyzer/ai/analyze/' + credentialId,
			{},
			headers
		);
		
		return request
			.then(function(response) {
				dispatch(aiAnalysisSuccess({
					analysis: response.data.analysis,
					timestamp: response.data.timestamp,
					usage: response.data.usage,
					cached: false
				}));
				
				dispatch(showMessage({
					message: 'AI analysis generated successfully',
					variant: 'success'
				}));
				
				return response.data;
			})
			.catch(function(err) {
				var errData = err.response && err.response.data ? err.response.data : { error: 'Failed to generate AI analysis' };
				
				dispatch(aiAnalysisFailed(errData.error));
				
				if (errData.rateLimited) {
					dispatch(showMessage({
						message: 'AI rate limit reached. Please try again later.',
						variant: 'warning'
					}));
				} else if (errData.aiEnabled === false) {
					dispatch(showMessage({
						message: 'AI is not configured. Please set up LLM in Settings.',
						variant: 'warning'
					}));
				} else {
					dispatch(showMessage({
						message: errData.error || 'Failed to generate AI analysis',
						variant: 'error'
					}));
				}
				
				throw err;
			});
	};
}
